import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Close, IosShare, MoreVert, AddBoxOutlined, AddToHomeScreen } from '@mui/icons-material';

const DISMISS_KEY = 'pwaInstallDismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () => (
  window.matchMedia?.('(display-mode: standalone)').matches
  || window.navigator.standalone === true
);

const detectPlatform = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  if (!isIOS && !isAndroid) return null;
  return isIOS ? 'ios' : 'android';
};

const AppBanner = () => {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (window.Capacitor?.isNativePlatform()) return undefined;
    if (isStandalone()) return undefined;

    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_DURATION) {
      return undefined;
    }

    const detected = detectPlatform();
    if (!detected) return undefined;

    setPlatform(detected);
    setOpen(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setOpen(false);
    setShowInstructions(false);
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') {
        setOpen(false);
      }
      return;
    }
    setShowInstructions(true);
  }, [deferredPrompt]);

  if (!open) return null;

  return (
    <Box
      sx={{
        width: '100%',
        flexShrink: 0,
        bgcolor: '#2a2118',
        color: 'common.white',
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        pt: 'calc(env(safe-area-inset-top) + 12px)',
        gap: 1,
        boxSizing: 'border-box',
        boxShadow: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <AddToHomeScreen sx={{ fontSize: 28, flexShrink: 0, color: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            Install Bodycount
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', lineHeight: 1.3 }}>
            Add it to your home screen for a full-app experience.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleInstall}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Install
        </Button>
        <IconButton size="small" onClick={handleDismiss} sx={{ color: 'inherit' }} aria-label="Dismiss">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {showInstructions && (
        <Box
          sx={{
            bgcolor: 'rgba(0,0,0,0.18)',
            borderRadius: 1,
            p: 1.25,
            mt: 0.5,
          }}
        >
          {platform === 'ios' ? (
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, lineHeight: 1.6 }}>
              In Safari, tap
              <IosShare fontSize="inherit" sx={{ mx: 0.25 }} />
              <strong>Share</strong>, then
              <AddBoxOutlined fontSize="inherit" sx={{ mx: 0.25 }} />
              <strong>Add to Home Screen</strong>.
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, lineHeight: 1.6 }}>
              In Chrome, tap
              <MoreVert fontSize="inherit" sx={{ mx: 0.25 }} />
              <strong>menu</strong>, then
              <strong>Install app</strong>
              {' '}
              or
              <strong>Add to Home screen</strong>.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AppBanner;
